// Snapshot stats for Metroid Prime: Origins slot saves (Samus / Fusion), taken at load.
// Collection circles are compared against a 100% catalog of known pickup field keys.

import { COLLECTION_CATALOG } from "./metroid_prime_origins_collection_catalog.js"
import { LOGBOOK_CATALOG } from "./metroid_prime_origins_logbook_catalog.js"
import { AEON_POWERS } from "./metroid_prime_origins_aeon_catalog.js"

const AREA_BY_PREFIX = {
  fri: "Frigate Orpheon",
  tal: "Tallon Overworld",
  cho: "Chozo Ruins",
  mag: "Magmoor Caverns",
  phe: "Phendrana Drifts",
  pha: "Phazon Mines",
  imp: "Impact Crater",
}

// Major equipment shown as named chips, a section per column. Each line is a row; `chained` rows
// read as progressions (beam -> combo), drawn with arrows between items. Keys are .NUT field names.
const equip = (...keys) => keys.map((key) => (typeof key === "string" ? { key, label: key } : key))

export const EQUIPMENT_LAYOUT = {
  suits: {
    label: "Suits",
    lines: [
      { items: equip("Varia Suit") },
      { items: equip("Gravity Suit") },
      { items: equip("Phazon Suit") },
    ],
  },
  beams: {
    label: "Beams",
    lines: [
      { chained: true, items: equip("Charge Beam", "Spazer") },
      { chained: true, items: equip("Wave Beam", "Wavebuster") },
      { chained: true, items: equip("Ice Beam", "Ice Spreader") },
      { chained: true, items: equip("Plasma Beam", "Incinerator") },
    ],
  },
  missiles: {
    label: "Missiles",
    lines: [{ chained: true, items: equip("Missile Launcher", "Super Missile") }],
  },
  morph: {
    label: "Morph Ball",
    lines: [
      { chained: true, items: equip("Morph Ball", "Boost Ball", "Spider Ball") },
      { chained: true, items: equip("Morph Ball Bomb", { key: "Power Bombs Max", label: "Power Bomb" }) },
    ],
  },
  other: {
    label: "Other",
    lines: [{ items: equip({ key: "Space Jump Boots", label: "Space Jump" }, "Grapple Beam", "Screw Attack") }],
  },
}

function fieldValue(fieldsByName, name) {
  const f = fieldsByName.get(name)
  return f == null ? null : f.value
}

function asNumber(value) {
  if (typeof value === "number" && Number.isFinite(value)) return value
  if (typeof value === "bigint") return Number(value)
  const n = Number(value)
  return Number.isFinite(n) ? n : 0
}

function isCollected(value) {
  return asNumber(value) !== 0
}

function humanizeRoomPart(part) {
  return String(part)
    .replace(/_/g, " ")
    .replace(/\bsub([A-Z])/g, "($1)")
    .replace(/\b\w/g, (ch) => ch.toUpperCase())
}

/** Area names in the game's order (Frigate Orpheon first). */
export const AREAS = Object.values(AREA_BY_PREFIX)

/**
 * Split a room code like `mag_Save_Station_Magmoor_A` into its area and room name.
 * @param {string} roomCode
 * @returns {{ area: string, room: string }}
 */
export function roomParts(roomCode) {
  const raw = String(roomCode || "").trim()
  if (!raw) return { area: "", room: "Unknown location" }

  const match = raw.match(/^([a-z]{3})_(.+)$/i)
  if (!match) return { area: "", room: humanizeRoomPart(raw) }

  const prefix = match[1].toLowerCase()
  return { area: AREA_BY_PREFIX[prefix] || prefix.toUpperCase(), room: humanizeRoomPart(match[2]) }
}

/**
 * Pretty-print a room code like `mag_Save_Station_Magmoor_A`.
 * @param {string} roomCode
 * @returns {string}
 */
export function prettyRoomName(roomCode) {
  const { area, room } = roomParts(roomCode)
  return area ? `${area} - ${room}` : room
}

/** `prefix` picks the clock: "" for in-game time, "Real " for wall-clock time (newer versions). */
function formatPlaytime(fieldsByName, prefix = "") {
  const part = (unit) => Math.max(0, Math.floor(asNumber(fieldValue(fieldsByName, `${prefix}${unit}`))))
  const hours = part("Hours")
  const minutes = part("Minutes")
  const seconds = part("Seconds")
  const parts = []
  if (hours > 0) parts.push(`${hours}h`)
  parts.push(`${String(minutes).padStart(hours > 0 ? 2 : 1, "0")}m`)
  parts.push(`${String(seconds).padStart(2, "0")}s`)
  return parts.join(" ")
}

function formatDeaths(fieldsByName) {
  return String(Math.max(0, Math.floor(asNumber(fieldValue(fieldsByName, "Deaths")))))
}

function formatSaveLocation(fieldsByName) {
  const room = String(fieldValue(fieldsByName, "Save Room") || "").trim()
  if (room && /^[a-z]{3}_/i.test(room)) return prettyRoomName(room)

  const area = String(
    fieldValue(fieldsByName, "Save Area") || fieldValue(fieldsByName, "Area Name") || "",
  ).trim()
  if (area && room) return `${area} - ${humanizeRoomPart(room)}`
  if (area) return area
  if (room) return humanizeRoomPart(room)
  return "Unknown"
}

function catalogPickupItems(fieldsByName, catalogItems) {
  return catalogItems
    .map((item) => {
      const field = fieldsByName.get(item.key)
      // Location keys are usually only written once collected.
      const collected = field ? isCollected(field.value) : false
      return {
        collected,
        roomCode: item.room,
        label: prettyRoomName(item.room),
        ...roomParts(item.room),
      }
    })
    .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: "base", numeric: true }))
}

function catalogArtifactItems(fieldsByName, catalogItems) {
  return catalogItems.map((item) => {
    const flag = fieldsByName.get(item.flag)
    const place = fieldsByName.get(item.key)
    const collected = flag ? isCollected(flag.value) : Boolean(place && isCollected(place.value))
    return {
      collected,
      roomCode: item.room,
      label: item.name,
      tip: prettyRoomName(item.room),
    }
  })
}

function mapEquipItem(fieldsByName, item) {
  return {
    collected: isCollected(fieldValue(fieldsByName, item.key)),
    label: item.label,
  }
}

function equipmentSections(fieldsByName) {
  return Object.entries(EQUIPMENT_LAYOUT).map(([id, section]) => {
    const lines = section.lines.map((line) => ({
      chained: Boolean(line.chained),
      items: line.items.map((item) => mapEquipItem(fieldsByName, item)),
    }))
    const flat = lines.flatMap((line) => line.items)
    return {
      id,
      label: section.label,
      collected: flat.filter((i) => i.collected).length,
      total: flat.length,
      lines,
    }
  })
}

function logbookSections(fieldsByName) {
  const scanSections = Object.entries(LOGBOOK_CATALOG).map(([id, section]) => {
    const items = section.entries.map((entry) => {
      const key = `${section.prefix}: ${entry}`
      return {
        collected: isCollected(fieldValue(fieldsByName, key)),
        label: entry,
      }
    })
    return {
      id,
      label: section.label,
      collected: items.filter((i) => i.collected).length,
      total: items.length,
      items,
    }
  })

  const artifactItems = catalogArtifactItems(fieldsByName, COLLECTION_CATALOG.artifacts.items)
  const artifacts = {
    id: "artifacts",
    label: "Chozo Artifacts",
    collected: artifactItems.filter((i) => i.collected).length,
    total: artifactItems.length,
    items: artifactItems,
  }

  return [...scanSections, artifacts]
}

// Counters shown as run stats, by group, as [field, label]. Older game versions lack some fields
// (Grapples, real time, sessions), so only fields the save has are listed.
const RUN_STAT_GROUPS = [
  {
    label: "Offense",
    stats: [
      ["Enemies Killed", "Enemies killed"],
      ["Damage Dealt", "Damage dealt"],
      ["Shots Fired", "Shots fired"],
      ["Missiles Fired", "Missiles fired"],
      ["Bombs Used", "Bombs used"],
      ["Power Bombs Used", "Power Bombs used"],
      ["Beam Changes", "Beam changes"],
    ],
  },
  {
    label: "Exploration",
    stats: [
      ["Distance Traveled", "Distance traveled"],
      ["Doors Opened", "Doors opened"],
      ["Blocks Broken", "Blocks broken"],
      ["Successful Scans", "Scans"],
      ["Successful Grapples", "Grapples"],
    ],
  },
  {
    label: "General",
    stats: [
      ["Damage Taken", "Damage taken"],
      ["Save Room Uses", "Save Station uses"],
      ["Sessions", "Sessions"],
    ],
  },
]

/** Run stat groups that have any fields in this save: [{ label, stats: [{ label, value }] }]. */
function runStats(fieldsByName) {
  const groups = RUN_STAT_GROUPS.map((group) => ({
    label: group.label,
    stats: group.stats.filter(([key]) => fieldsByName.has(key)).map(([key, label]) => ({
      label,
      value: Math.round(asNumber(fieldValue(fieldsByName, key))).toLocaleString("en-US"),
    })),
  }))
  // Wall-clock play time (newer versions) leads General, ahead of the counters.
  if (fieldsByName.has("Real Hours")) {
    groups.find((group) => group.label === "General").stats.unshift({ label: "Real time", value: formatPlaytime(fieldsByName, "Real ") })
  }
  return groups.filter((group) => group.stats.length)
}

const arrayValues = (value) => (Array.isArray(value) ? value.map((entry) => entry?.value ?? entry) : [])

/**
 * Aeon Powers are Remix-only: the `Aeon` array lists those collected, in pickup order, and
 * `Aeon Status` runs parallel to it with 1 for each one equipped. Null for Classic runs and saves
 * with no Aeon array. Names the catalog doesn't know (a newer game version) are kept at the end.
 */
function aeonPowers(fieldsByName, classicMode) {
  const value = fieldValue(fieldsByName, "Aeon")
  if (classicMode || !Array.isArray(value)) return null
  const owned = arrayValues(value).map(String)
  const status = arrayValues(fieldValue(fieldsByName, "Aeon Status"))
  const equipped = new Set(owned.filter((_, i) => isCollected(status[i])))
  const names = [...AEON_POWERS, ...owned.filter((name) => !AEON_POWERS.includes(name))]
  const items = names.map((label) => ({ label, collected: owned.includes(label), equipped: equipped.has(label) }))
  return {
    collected: items.filter((i) => i.collected).length,
    total: items.length,
    equipped: equipped.size,
    items,
  }
}

/** What a new game's slot save holds, for browsing with no file (see a fresh `Samus <slot>.NUT`). */
export const EMPTY_SLOT_DOC = {
  fields: [
    ["Hours", 0], ["Minutes", 0], ["Seconds", 0], ["Deaths", 0], ["Classic Mode", 0],
    ["Save Room", "fri_Exterior_Docking_Hangar"], ["Total Energy", 99], ["Missiles", 15],
    ["Power Suit", 1], ["Varia Suit", 1], ["Power Beam", 1], ["Morph Ball", 1], ["Morph Ball Bomb", 1],
    ["Missile Launcher", 1], ["Grapple Beam", 1], ["Aeon", []], ["Aeon Status", []],
  ].map(([name, value]) => ({ name, value })),
}

/**
 * @param {{ fields: Array<{ name: string, value: unknown }> }} doc
 * @returns {{ playtime: string, deaths: string, location: string, gameMode: string, equipment: Array, runStats: Array, aeon: object | null, collections: Array, logbook: { collected: number, total: number, sections: Array } } | null}
 */
export function extractSlotStats(doc) {
  const fields = Array.isArray(doc?.fields) ? doc.fields : []
  if (fields.length === 0) return null

  const fieldsByName = new Map(fields.map((f) => [f.name, f]))
  // Slot saves always have playtime / save room style keys.
  if (!fieldsByName.has("Hours") && !fieldsByName.has("Save Room") && !fieldsByName.has("Missiles Max")) {
    return null
  }

  const collections = Object.entries(COLLECTION_CATALOG)
    .filter(([id]) => id !== "artifacts")
    .map(([id, group]) => {
      const items = catalogPickupItems(fieldsByName, group.items)
      return {
        id,
        label: group.label,
        collected: items.filter((i) => i.collected).length,
        total: items.length,
        items,
      }
    })

  const byCollectionId = Object.fromEntries(collections.map((c) => [c.id, c]))
  const energyTanks = byCollectionId["energy-tanks"]?.collected ?? 0
  const missileExpansions = byCollectionId.missiles?.collected ?? 0
  const powerBombExpansions = byCollectionId["power-bombs"]?.collected ?? 0
  // Origins matches classic Metroid Prime: base 99 energy, +100 per tank.
  // Missile / PB capacity is expansions only (×5 / ×2) — no separate launcher/ability base.
  // Current amounts come from the slot fields at save time.
  const capacities = {
    health: {
      current: asNumber(fieldValue(fieldsByName, "Total Energy")),
      max: 99 + energyTanks * 100,
    },
    missiles: {
      current: asNumber(fieldValue(fieldsByName, "Missiles")),
      max: missileExpansions * 5,
    },
    powerBombs: {
      current: asNumber(fieldValue(fieldsByName, "Power Bombs")),
      max: powerBombExpansions * 2,
    },
  }

  const logbookSectionsList = logbookSections(fieldsByName)
  // The game's own Logbook count leaves the artifacts out (they're also tracked on their own).
  const scanned = logbookSectionsList.filter((s) => s.id !== "artifacts")
  const logbookCollected = scanned.reduce((sum, s) => sum + s.collected, 0)
  const logbookTotal = scanned.reduce((sum, s) => sum + s.total, 0)

  const classicMode = asNumber(fieldValue(fieldsByName, "Classic Mode")) === 1

  return {
    playtime: formatPlaytime(fieldsByName),
    deaths: formatDeaths(fieldsByName),
    location: formatSaveLocation(fieldsByName),
    gameMode: classicMode ? "Classic" : "Remix",
    capacities,
    equipment: equipmentSections(fieldsByName),
    runStats: runStats(fieldsByName),
    aeon: aeonPowers(fieldsByName, classicMode),
    collections,
    logbook: {
      collected: logbookCollected,
      total: logbookTotal,
      sections: logbookSectionsList,
    },
  }
}
